import React, { Fragment, useState, useCallback, useEffect, FC } from 'react';
import { DateTimePicker } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { time } from '~/utils';
import cx from 'classnames';
import { SelectInput, TextInput, AddFilterAction } from '~/components';
import { filterChange, selectors, cleanupFilters } from '~/ducks/virtualizedList';
import { ActionReport, Moment } from '~/types';
import { statusLabel } from '~/components/StatusField';
import { useAllReportSystemsAndTypes } from '~/hooks/actionReports';
import { useTranslateActionReports } from '.';
import { isEmpty } from 'lodash';
import { IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';
import { useListFilterStyles } from '~/hooks/useListFilterStyles';
import { ACTION_REPORTS } from '~/utils/constants/resources';
import { hasCurrentLocale12HourFormat } from '~/utils/moment';

interface FiltersProps {
  cleanFilters?: boolean;
}

const resource = ACTION_REPORTS;

const Filters: FC<FiltersProps> = (props) => {
  const { cleanFilters = true } = props;
  const classes = useStyles();
  const listFilterClasses = useListFilterStyles();
  const dispatch = useDispatch();
  const t = useTranslateActionReports();

  const filters = useSelector(selectors.getFilters(resource));
  const [localValues, setLocalValues] = useState(filters);
  const [addedFilters, setAddedFilters] = useState<string[]>(
    Object.keys(filters).filter((k) => {
      return !!(filters as any)[k];
    })
  );

  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  const dispatchFilterChange = (name: string, value: string | Moment | undefined) => {
    dispatch(filterChange({ resource, filters: { [name]: value || null } }));
  };

  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 1000), []);
  const debouncedHandleInput = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({
      ...localValues,
      [name]: value,
    });

    debouncedDispatchFilterChange(name, value);
  };

  const filtersAreEmpty = isEmpty(filters);
  useEffect(() => {
    if (cleanFilters || filtersAreEmpty) {
      dispatch(cleanupFilters({ resource }));
      dispatch(
        filterChange({
          resource,
          filters: {
            createdFrom: time().subtract(1, 'months'),
            createdTo: time(),
          },
        })
      );
      setAddedFilters([]);
    }
  }, [dispatch, cleanFilters, filtersAreEmpty]);

  const handleSelectInput = (e: any) => {
    const { name, value } = e.target;

    setLocalValues({
      ...localValues,
      [name]: value,
    });

    dispatchFilterChange(name, value);
  };

  const hideFilter = (filter: string) => {
    const inputName = filter;
    dispatchFilterChange(inputName, '');
    setAddedFilters(addedFilters.filter((f) => f !== filter));
  };

  const handleSystemChange = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({
      ...localValues,
      [name]: value,
      reportType: '',
    });
    dispatchFilterChange(name, value);
    dispatchFilterChange('reportType', '');
  };
  const handleDateTimeChange = (data: Moment, inputType: string) => {
    setLocalValues({
      ...localValues,
      [inputType]: data,
    });
    dispatchFilterChange(inputType, data);
  };

  const schemaQuery = useAllReportSystemsAndTypes();
  const getSystemOptions = () => {
    return (
      schemaQuery.data?.map(({ system }) => ({
        value: system,
        text: system,
      })) || []
    );
  };
  const getReportTypeOptions = (system: string | undefined) => {
    if (!system) return [];
    return (
      schemaQuery.data
        ?.find((item) => item.system === system)
        ?.reportTypes.map((reportType) => ({
          value: reportType,
          text: reportType,
        })) || []
    );
  };

  const allFilters = [
    'referenceId',
    'objectType',
    'objectId',
    'hasWarnings',
    'system',
    'reportType',
    'status',
  ];

  const objectTypeOptions = [
    { value: 'HEAD_TRANSACTION', text: t('HEAD_TRANSACTION') },
    { value: 'INDIVIDUAL', text: t('INDIVIDUAL') },
  ];

  const renderAddedFilter = (key: string) => {
    switch (key) {
      case 'objectType':
        return (
          <SelectInput<ActionReport['objectType'] | undefined>
            label={t('objectType')}
            name='objectType'
            value={localValues.objectType}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            options={objectTypeOptions as any}
            onChange={handleSelectInput}
            allowEmpty
          />
        );
      case 'objectId':
        return (
          <TextInput
            name='objectId'
            onChange={debouncedHandleInput}
            value={localValues.objectId}
            label={t('objectId')}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            disableHelperText
          />
        );
      case 'hasWarnings':
        return (
          <SelectInput<string>
            label={t('hasWarnings')}
            name='hasWarnings'
            value={localValues.hasWarnings + ''}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            options={[
              {
                value: 'true',
                text: t('hasSomeWarnings'),
              },
              {
                value: 'false',
                text: t('hasNoWarnings'),
              },
            ]}
            onChange={handleSelectInput}
            allowEmpty
          />
        );
      case 'referenceId':
        return (
          <TextInput
            name='referenceId'
            onChange={debouncedHandleInput}
            value={localValues.referenceId}
            label={t('referenceId')}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            disableHelperText
          />
        );
      case 'system':
        return (
          <SelectInput<ActionReport['system'] | undefined>
            label={t('system')}
            name='system'
            value={localValues.system}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            options={getSystemOptions()}
            onChange={handleSystemChange}
            disabled={schemaQuery.isLoading}
            allowEmpty
          />
        );
      case 'reportType':
        return (
          <SelectInput<ActionReport['reportType'] | undefined>
            label={t('reportType')}
            name='reportType'
            value={localValues.reportType}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            options={getReportTypeOptions(localValues.system)}
            onChange={handleSelectInput}
            disabled={schemaQuery.isLoading || !localValues.system}
            allowEmpty
            defaultValue=''
          />
        );
      case 'status':
        return (
          <ReportStatusSelectInput
            name='status'
            value={localValues.status}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            onChange={handleSelectInput}
          />
        );
      default:
        return null;
    }
  };

  // Render added filters before the boolean switch
  // to ensure the switch is always at the end of the filter list
  // and to avoid missmathced filter widths
  const renderAddedFilters = () =>
    addedFilters.map((filter) => {
      const component = renderAddedFilter(filter);
      if (component) {
        return (
          <div className={listFilterClasses.flexContainer} key={filter}>
            {component}
            <IconButton
              aria-label='delete'
              className={listFilterClasses.deleteIcon}
              onClick={() => hideFilter(filter)}
            >
              <DeleteIcon />
            </IconButton>
          </div>
        );
      } else {
        return null;
      }
    });

  return (
    <Fragment>
      {filters && (
        <div className={listFilterClasses.container}>
          <div className={classes.leftSide}>
            <div className={listFilterClasses.inputsGroup}>
              <DateTimePicker
                className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                label={t('createdFrom')}
                value={localValues.createdFrom || null}
                onChange={(date) => handleDateTimeChange(date as Moment, 'createdFrom')}
                maxDate={localValues.createdTo || time().format()}
                clearable
              />
              <DateTimePicker
                className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                label={t('createdTo')}
                value={localValues.createdTo || null}
                onChange={(date) => handleDateTimeChange(date as Moment, 'createdTo')}
                maxDate={time().format()}
                minDate={localValues.createdFrom || undefined}
                clearable
              />
              {renderAddedFilters()}
            </div>
          </div>
          <div className={classes.rightSide}>
            <AddFilterAction
              filters={allFilters
                .filter((f) => !addedFilters.includes(f))
                .sort()
                .map((filterKey) => ({
                  key: filterKey,
                  label: t(filterKey),
                }))}
              onFilterSelected={(filter: string) => {
                setAddedFilters(addedFilters.concat([filter]));
              }}
            />
          </div>
        </div>
      )}
    </Fragment>
  );
};

export const ReportStatusSelectInput: FC<{
  name: string;
  label?: string;
  value: ActionReport['status'] | undefined;
  onChange: (e: any) => void;
  className?: string;
  [x: string]: any;
}> = (props) => {
  const { name, value, onChange, className, label, ...rest } = props;
  const translate = useTranslate();
  const t = useTranslateActionReports();

  return (
    <SelectInput<ActionReport['status'] | undefined>
      label={label || t('status')}
      name={name}
      value={value}
      className={cx(className)}
      options={[
        { value: 'ACCEPTED', text: translate(statusLabel('ACCEPTED')) },
        { value: 'FAILED', text: translate(statusLabel('FAILED')) },
        { value: 'INITIAL', text: translate(statusLabel('INITIAL')) },
        { value: 'INVALID', text: translate(statusLabel('INVALID')) },
        { value: 'PENDING', text: translate(statusLabel('PENDING')) },
        { value: 'REJECTED', text: translate(statusLabel('REJECTED')) },
        { value: 'SUBMITTED', text: translate(statusLabel('SUBMITTED')) },
        { value: 'REVOKED', text: translate(statusLabel('REVOKED')) },
      ]}
      allowEmpty
      onChange={onChange}
      {...rest}
    />
  );
};

const useStyles = makeStyles(() => {
  return {
    leftSide: {
      maxWidth: 'calc(100% - 150px)',
    },
    // Can change width to fit export or other buttons on the right side
    rightSide: { minWidth: 150, textAlign: 'right' },
  };
});

export default Filters;
